import axios from 'axios'

import { ITemplateForm } from '@core/entities/template/TemplateForm'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup'
import { SubmissionProgressReporter } from '@core/react-query/defaultMutations'
import {
  CreateFileUploadProgressReporter,
  createUploadProgressReporter,
  doesProofNeedUpload,
  s3Upload,
} from '@core/react-query/helpers'
import { createRequestErrorFromErrors } from '@core/utils/errors'
import { RequestTypes } from './requestTypes'

export const uploadProofs = async (
  proofs: any[],
  folderName: string,
  createFileUploadProgressReporter?: CreateFileUploadProgressReporter,
  signal?: AbortSignal,
): Promise<any[]> => {
  const errors: any[] = []
  const failedToUploadPaths: string[] = []

  // On the web, we want to show the failed to upload image paths to the user, but on the mobile
  // that doesn't make much sense, so we just show the generic error message
  const getErrorMessagePostfix = () =>
    API.isMobile() ? '' : `: ${failedToUploadPaths.join(', ')}`

  // First, try fetching all proofs
  const verifiedProofs = await Promise.all(
    proofs.map(async (proof) => {
      // An annotated proof that lost its Blob still has the bytes behind `uri`
      if (!(proof instanceof Blob) && doesProofNeedUpload(proof)) {
        try {
          if (API.isMobile()) {
            return proof
          } else {
            // Attempt to fetch the proof using the URI
            const response = await axios.get(proof.uri, {
              responseType: 'blob',
            })

            const file = new File(
              [response.data],
              proof.path ?? proof.name ?? 'attachment',
              { type: proof.type },
            ) as File & { siteBoxDocumentVersionId?: number }
            file.siteBoxDocumentVersionId =
              proof.siteBoxDocumentVersionId ??
              proof.siteBoxDocument?.currentVersion?.id
            return file
          }
        } catch (error) {
          errors.push(error)
          failedToUploadPaths.push(API.isMobile() ? proof.name : proof.path)
        }
      }
      return proof
    }),
  )

  if (errors.length > 0) {
    throw createRequestErrorFromErrors(errors, {
      errorMessagePostfix: getErrorMessagePostfix(),
    })
  }

  const uploadProof = async (proof: any) => {
    if (!doesProofNeedUpload(proof)) {
      const versionId =
        proof.siteBoxDocumentVersionId ||
        proof.siteBoxDocument?.currentVersion?.id

      if (versionId) {
        return {
          key: proof.key,
          siteBoxDocumentVersionId: versionId,
        }
      }
      return proof
    }

    try {
      const onUploadProgress = createFileUploadProgressReporter?.()

      // Use the Blob we fetched earlier for uploading
      const uploadedFileKey = await s3Upload({
        file: proof, // Use the blob obtained from the fetch step
        folderName,
        coordinates: proof.coordinates,
        onUploadProgress,
        signal,
      })

      const uploaded = { ...proof, key: uploadedFileKey }
      const siteBoxDocumentVersionId =
        proof.siteBoxDocumentVersionId ||
        proof.siteBoxDocument?.currentVersion?.id

      // Keep the SiteBox version as provenance, while the new key points to
      // the annotated copy that should be displayed.
      delete uploaded.siteBoxDocument
      if (siteBoxDocumentVersionId) {
        uploaded.siteBoxDocumentVersionId = siteBoxDocumentVersionId
      }
      return uploaded
    } catch (error) {
      errors.push(error)
      failedToUploadPaths.push(API.isMobile() ? proof.name : proof.path)
    }
  }

  let uploadedProofs = []

  if (API.isMobile()) {
    // NOTE: Sequentially upload for mobile for better performance in bad internet conditions
    for (const proof of verifiedProofs) {
      const result = await uploadProof(proof)

      if (result) {
        uploadedProofs.push(result)
      } else {
        // Stop uploading on the first failed upload
        break
      }
    }
  } else {
    const uploads = await Promise.all(verifiedProofs.map(uploadProof))
    uploadedProofs = uploads.filter(Boolean)
  }

  // If any errors occurred during the S3 upload, throw them all at once
  if (errors.length > 0) {
    throw createRequestErrorFromErrors(errors, {
      errorMessagePostfix: getErrorMessagePostfix(),
    })
  }

  return uploadedProofs
}

// Add this new helper function
const handleInspectionItemAnswersUpload = async (
  inspectionItemAnswers: any[],
  templateId: number,
  createFileUploadProgressReporter?: CreateFileUploadProgressReporter,
  signal?: AbortSignal,
) => {
  if (!inspectionItemAnswers?.length) return []

  const uploadInspectionItemAnswers = async (inspectionAnswer) => {
    const updatedAnswer = { ...inspectionAnswer }

    // Handle submissionProofs if they exist
    if (inspectionAnswer.submissionProofs?.length) {
      const uploadedProofs = await uploadProofs(
        inspectionAnswer.submissionProofs,
        `${templateId}-inspection-proof`,
        createFileUploadProgressReporter,
        signal,
      )
      updatedAnswer.submissionProofs = uploadedProofs
    }

    if (inspectionAnswer.submissionDocuments?.length) {
      const uploadedDocuments = await uploadProofs(
        inspectionAnswer.submissionDocuments,
        `${templateId}-inspection-documents`,
        createFileUploadProgressReporter,
        signal,
      )
      updatedAnswer.submissionDocuments = uploadedDocuments
    }

    // Handle attachments if they exist
    if (inspectionAnswer.taskPayload?.attachments?.data?.length) {
      const uploadedProofs = await uploadProofs(
        inspectionAnswer.taskPayload.attachments.data,
        `${templateId}-inspection-task-attachment`,
        createFileUploadProgressReporter,
        signal,
      )
      updatedAnswer.taskPayload = {
        ...inspectionAnswer.taskPayload,
        attachments: { data: uploadedProofs },
      }
    }

    if (updatedAnswer.taskPayload?.stepAnswers?.answers?.length) {
      const uploadedStepAnswers = await uploadTemplateAnswerFiles({
        answers: updatedAnswer.taskPayload.stepAnswers.answers,
        templateId,
        createFileUploadProgressReporter,
        signal,
      })

      updatedAnswer.taskPayload = {
        ...updatedAnswer.taskPayload,
        stepAnswers: {
          ...updatedAnswer.taskPayload.stepAnswers,
          answers: uploadedStepAnswers,
        },
      }
    }

    return updatedAnswer
  }

  let uploadedInspectionItemAnswers = []

  if (API.isMobile()) {
    // NOTE: Sequentially upload for mobile for better performance in bad internet conditions
    for (const answer of inspectionItemAnswers) {
      const result = await uploadInspectionItemAnswers(answer)
      uploadedInspectionItemAnswers.push(result)
    }
  } else {
    uploadedInspectionItemAnswers = await Promise.all(
      inspectionItemAnswers.map(uploadInspectionItemAnswers),
    )
  }

  return uploadedInspectionItemAnswers
}

export const uploadTemplateAnswerFiles = async ({
  answers,
  templateId,
  onSubmissionProgress,
  createFileUploadProgressReporter,
  signal,
}: {
  answers: Array<ITemplateQuestionGroup>
  templateId: number
  /** End-to-end submission reporter — the helper weighs the files in `answers`
   * and reports the upload window itself. The entry point for mutationFns. */
  onSubmissionProgress?: SubmissionProgressReporter
  /** Per-file reporter factory for recursion and combined multi-batch uploads
   * (see taskKeys). Wins over onSubmissionProgress when both are provided. */
  createFileUploadProgressReporter?: CreateFileUploadProgressReporter
  /** Aborts the remaining uploads when a live submission is cancelled. */
  signal?: AbortSignal
}): Promise<Array<ITemplateQuestionGroup>> => {
  if (!answers) return []

  const fileUploadProgressReporterFactory =
    createFileUploadProgressReporter ??
    createUploadProgressReporter(answers, onSubmissionProgress)

  const uploadGroup = async (group: ITemplateQuestionGroup) => {
    const uploadQuestion = async (question: any) => {
      const uploadedQuestion = { ...question, signatureKey: undefined }

      // Handle signature upload
      if (question.localSignature) {
        const onUploadProgress = fileUploadProgressReporterFactory?.()
        const uploadedFileKey = await s3Upload({
          file: question.localSignature,
          folderName: `${templateId}-signature`,
          onUploadProgress,
          signal,
        })
        uploadedQuestion.signatureKey = uploadedFileKey
      } else if (question.signatureUrl) {
        uploadedQuestion.signatureKey = question.signatureKey
      }

      // Handle submission proofs upload
      if (question.submissionProofs) {
        const uploadedProofs = await uploadProofs(
          question.submissionProofs,
          `${templateId}-proof`,
          fileUploadProgressReporterFactory,
          signal,
        )
        uploadedQuestion.submissionProofs = uploadedProofs
      }

      // Handle submission documents upload
      if (question.submissionDocuments) {
        const uploadedDocuments = await uploadProofs(
          question.submissionDocuments,
          `${templateId}-documents`,
          fileUploadProgressReporterFactory,
          signal,
        )
        uploadedQuestion.submissionDocuments = uploadedDocuments
      }

      // Handle acknowledge information document upload
      if (question.acknowledgeInformationDocument) {
        const [uploadedProof] = await uploadProofs(
          [question.acknowledgeInformationDocument],
          `${templateId}-documents`,
          fileUploadProgressReporterFactory,
          signal,
        )
        uploadedQuestion.acknowledgeInformationDocumentKey = uploadedProof.key
        delete question.acknowledgeInformationDocument
      }

      // Replace the inspection item answers handling with the new helper function
      if (question.inspectionItemAnswers?.length) {
        uploadedQuestion.inspectionItemAnswers =
          await handleInspectionItemAnswersUpload(
            question.inspectionItemAnswers,
            templateId,
            fileUploadProgressReporterFactory,
            signal,
          )
      }

      return uploadedQuestion
    }

    let uploadedGroupData = []

    if (API.isMobile()) {
      // NOTE: Sequentially upload for mobile for better performance in bad internet conditions
      for (const question of group.data) {
        const result = await uploadQuestion(question)
        uploadedGroupData.push(result)
      }
    } else {
      uploadedGroupData = await Promise.all(group.data.map(uploadQuestion))
    }

    return { ...group, data: uploadedGroupData }
  }

  let uploadedAnswers = []

  if (API.isMobile()) {
    // NOTE: Sequentially upload for mobile for better performance in bad internet conditions
    for (const answer of answers) {
      const result = await uploadGroup(answer)
      uploadedAnswers.push(result)
    }
  } else {
    uploadedAnswers = await Promise.all(answers.map(uploadGroup))
  }

  return uploadedAnswers
}

export const uploadTemplateApprovalAnswerFiles = async <
  T extends { approvalQuestions?: ITemplateForm },
>({
  approvals,
  templateId,
}: {
  approvals: T[]
  templateId: number
}): Promise<T[]> => {
  return Promise.all(
    approvals.map(async (approval) => ({
      ...approval,
      ...(approval.approvalQuestions && {
        approvalQuestions: {
          ...approval.approvalQuestions,
          questions: await uploadTemplateAnswerFiles({
            answers: approval.approvalQuestions.questions,
            templateId,
          }),
        },
      }),
    })),
  )
}

export const uploadCheckInSignatures = (
  users: RequestTypes['checkIn']['users'],
  folderName: string,
) =>
  Promise.all(
    users.map(async ({ userId, signature }) => ({
      userId,
      signatureKey: await s3Upload({ file: signature, folderName }),
    })),
  )
